import React, { useState } from "react";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { Check, Loader2, LogOut } from "lucide-react";
import { useNavigate } from "react-router-dom";

const ACCENTS = [
  { key: "sunset-orange", name: "Sunset Orange", hsl: "21 100% 50%" },
  { key: "canyon-red", name: "Canyon Red", hsl: "0 66% 51%" },
  { key: "mesa-gold", name: "Mesa Gold", hsl: "45 96% 58%" },
  { key: "plateau-blue", name: "Plateau Blue", hsl: "210 78% 47%" },
];

export default function Settings() {
  const { user, updateProfile, logout } = useAuth();
  const navigate = useNavigate();
  const [name, setName] = useState(user?.name || "");
  const [headline, setHeadline] = useState(user?.headline || "");
  const [bio, setBio] = useState(user?.bio || "");
  const [location, setLocation] = useState(user?.location || "");
  const [accent, setAccent] = useState(user?.accent_color || "sunset-orange");
  const [saving, setSaving] = useState(false);

  // Apply accent instantly for preview
  const applyAccent = (a) => {
    setAccent(a);
    document.documentElement.setAttribute("data-accent", a);
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await updateProfile({ name, headline, bio, location, accent_color: accent });
      toast.success("Profile updated");
    } catch (err) {
      const detail = err?.response?.data?.detail || "Could not save";
      toast.error(typeof detail === "string" ? detail : "Please check your inputs");
    } finally { setSaving(false); }
  };

  return (
    <div>
      <div className="mb-8">
        <div className="text-xs uppercase tracking-[0.3em] text-neutral-400 mb-3">Profile & Settings</div>
        <h1 className="font-display text-4xl sm:text-5xl tracking-tighter leading-none font-medium">Your Film Front.</h1>
      </div>

      <div className="grid lg:grid-cols-[1fr_360px] gap-8">
        <form onSubmit={save} className="ff-card p-8 space-y-5" data-testid="settings-form">
          <div className="grid md:grid-cols-2 gap-4">
            <label className="block">
              <span className="text-xs uppercase tracking-widest text-neutral-400">Name</span>
              <input value={name} onChange={(e) => setName(e.target.value)} className="mt-1.5 w-full bg-black border border-white/10 rounded-lg px-3 py-2.5 outline-none focus:border-[hsl(var(--ff-accent))]" data-testid="settings-name-input" />
            </label>
            <label className="block">
              <span className="text-xs uppercase tracking-widest text-neutral-400">Location</span>
              <input value={location} onChange={(e) => setLocation(e.target.value)} className="mt-1.5 w-full bg-black border border-white/10 rounded-lg px-3 py-2.5 outline-none focus:border-[hsl(var(--ff-accent))]" data-testid="settings-location-input" />
            </label>
          </div>
          <label className="block">
            <span className="text-xs uppercase tracking-widest text-neutral-400">Headline</span>
            <input value={headline} onChange={(e) => setHeadline(e.target.value)} placeholder="e.g. DP · Camera Op · Southern Utah" className="mt-1.5 w-full bg-black border border-white/10 rounded-lg px-3 py-2.5 outline-none focus:border-[hsl(var(--ff-accent))]" data-testid="settings-headline-input" />
          </label>
          <label className="block">
            <span className="text-xs uppercase tracking-widest text-neutral-400">Bio</span>
            <textarea value={bio} onChange={(e) => setBio(e.target.value)} rows={5} placeholder="What do you make? What are you looking for?" className="mt-1.5 w-full bg-black border border-white/10 rounded-lg px-3 py-2.5 outline-none focus:border-[hsl(var(--ff-accent))]" data-testid="settings-bio-input" />
          </label>

          <div>
            <div className="text-xs uppercase tracking-widest text-neutral-400 mb-3">Accent color</div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="settings-accent-picker">
              {ACCENTS.map((a) => {
                const active = accent === a.key;
                return (
                  <button
                    key={a.key}
                    type="button"
                    onClick={() => applyAccent(a.key)}
                    className={`ff-card p-4 flex items-center gap-3 ${active ? "ring-2 ring-[hsl(var(--ff-accent))]" : ""}`}
                    data-testid={`settings-accent-${a.key}`}
                  >
                    <div className="w-6 h-6 rounded-full" style={{ background: `hsl(${a.hsl})` }} />
                    <div className="flex-1 text-left">
                      <div className="text-sm">{a.name}</div>
                    </div>
                    {active && <Check className="w-4 h-4 ff-accent-text" />}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="flex gap-3 pt-2">
            <button type="submit" disabled={saving} className="ff-btn-primary disabled:opacity-60" data-testid="settings-save-btn">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save changes"}
            </button>
            <button
              type="button"
              onClick={() => { logout(); navigate("/"); }}
              className="ff-btn-ghost text-sm"
              data-testid="settings-signout-btn"
            >
              <LogOut className="w-4 h-4 mr-2" /> Sign out
            </button>
          </div>
        </form>

        <aside className="space-y-4">
          <div className="ff-card p-6">
            <div className="text-xs uppercase tracking-[0.25em] text-neutral-400 mb-3">Account</div>
            <div className="text-sm text-neutral-300">
              <div><span className="text-neutral-500">Email:</span> {user?.email}</div>
              <div className="mt-1"><span className="text-neutral-500">Tier:</span> {user?.membership_tier || "Free"}</div>
              <div className="mt-1"><span className="text-neutral-500">Verified:</span> {user?.verified ? "Yes" : "No"}</div>
              <div className="mt-1"><span className="text-neutral-500">Email verified:</span> {user?.email_verified ? "Yes" : "Pending"}</div>
            </div>
          </div>
          <div className="ff-card p-6">
            <div className="text-xs uppercase tracking-[0.25em] text-neutral-400 mb-3">Mobile</div>
            <div className="text-sm text-neutral-300">Push notifications, in-app messaging, camera uploads, and offline access are all in the Film Front iOS & Android app.</div>
          </div>
        </aside>
      </div>
    </div>
  );
}
